import { useCallback, useEffect, useMemo, useState } from 'react'
import { X } from 'lucide-react'
import { toast } from 'sonner'
import { IconButton } from '../ui/IconButton.jsx'
import { Button } from '../ui/Button.jsx'
import { cn } from '../../lib/cn.js'
import { useIntelligentWeb } from '../../context/IntelligentWebContext.jsx'
import { useOrderStatus } from '../../context/OrderStatusContext.jsx'
import { postWidgetOrderStatusLookup } from '../../lib/widgetOrderStatusApi.js'
import { t, verificationLabel } from '../../lib/widgetLocale.js'

/**
 * In-panel order status lookup (mirrors lead-form overlay placement).
 */
export function OrderStatusInPanel({ conf, onResult }) {
  const { apiBaseUrl } = useIntelligentWeb()
  const { isOrderStatusOpen, closeOrderStatus } = useOrderStatus()
  const os = conf?.orderStatus
  const lang = conf?.siteLanguage
  const allowed = Array.isArray(os?.verificationAllowed)
    ? os.verificationAllowed
    : ['email']

  const [orderNumber, setOrderNumber] = useState('')
  const [verificationType, setVerificationType] = useState(allowed[0] || 'email')
  const [verificationValue, setVerificationValue] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!isOrderStatusOpen) {
      setOrderNumber('')
      setVerificationValue('')
      setVerificationType(allowed[0] || 'email')
    }
  }, [isOrderStatusOpen, allowed])

  useEffect(() => {
    if (allowed.length && !allowed.includes(verificationType)) {
      setVerificationType(allowed[0])
    }
  }, [allowed, verificationType])

  const title = os?.formTitle || t('orderStatus.formTitle', lang)
  const subtitle = os?.formSubtitle || t('orderStatus.formSubtitle', lang)
  const orderLabel = os?.orderNumberLabel || t('orderStatus.orderNumberLabel', lang)
  const submitLabel = os?.submitLabel || t('orderStatus.submitLabel', lang)

  const canSubmit = useMemo(
    () =>
      Boolean(
        orderNumber.trim() &&
          verificationValue.trim() &&
          allowed.includes(verificationType),
      ),
    [orderNumber, verificationValue, verificationType, allowed],
  )

  const submit = useCallback(
    async (e) => {
      e.preventDefault()
      if (!canSubmit || submitting) return
      setSubmitting(true)
      try {
        const res = await postWidgetOrderStatusLookup({
          apiBaseUrl,
          conf,
          orderNumber: orderNumber.trim(),
          verificationType,
          verificationValue: verificationValue.trim(),
        })
        closeOrderStatus()
        onResult?.({
          messageHtml: res.messageHtml,
          buttons: res.buttons,
        })
      } catch (err) {
        console.error(err)
        toast.error(
          err instanceof Error ? err.message : t('orderStatus.lookupFailed', lang),
        )
      } finally {
        setSubmitting(false)
      }
    },
    [
      apiBaseUrl,
      conf,
      orderNumber,
      verificationType,
      verificationValue,
      canSubmit,
      submitting,
      closeOrderStatus,
      onResult,
    ],
  )

  if (!isOrderStatusOpen) return null

  const shell = (body) => (
    <>
      <div
        className={cn(
          'absolute inset-0 z-[15] cursor-default bg-black/40 backdrop-blur-[1.5px]',
          'dark:bg-black/55',
        )}
        aria-hidden="true"
        onClick={closeOrderStatus}
      />
      <div className="pointer-events-none absolute inset-0 z-20 flex min-h-0 items-center justify-center p-2 sm:p-3">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="ometrics-order-status-title"
          className={cn(
            'pointer-events-auto flex max-h-full min-h-0 w-full max-w-[min(420px,calc(100%-0.25rem))] flex-col overflow-hidden rounded-xl border border-[var(--color-border)]',
            'bg-[var(--color-surface)] shadow-[0_16px_48px_rgba(0,0,0,0.22)]',
            'dark:shadow-[0_16px_48px_rgba(0,0,0,0.55)]',
          )}
        >
          <div
            className={cn(
              'flex shrink-0 items-start gap-3 border-b border-[var(--color-border)]/80',
              'bg-gradient-to-b from-[var(--color-muted)]/35 to-transparent px-4 pb-4 pt-3',
            )}
          >
            <div className="min-w-0 flex-1 space-y-1">
              <h2
                id="ometrics-order-status-title"
                className="text-lg font-semibold leading-snug tracking-tight text-[var(--color-surface-foreground)] sm:text-xl"
              >
                {title}
              </h2>
              {subtitle ? (
                <p className="text-sm leading-relaxed text-[var(--color-muted-foreground)] sm:text-[15px]">
                  {subtitle}
                </p>
              ) : null}
            </div>
            <IconButton
              aria-label="Close"
              onClick={closeOrderStatus}
              className="shrink-0 text-[var(--color-muted-foreground)] hover:text-[var(--color-surface-foreground)]"
            >
              <X className="h-4 w-4" />
            </IconButton>
          </div>
          {body}
        </div>
      </div>
    </>
  )

  if (!os?.enabled) {
    return shell(
      <div className="flex flex-col gap-3 px-4 py-4">
        <p className="text-sm leading-relaxed text-[var(--color-muted-foreground)]">
          Order status lookup is not enabled for this assistant. Turn it on under
          Settings → eCommerce → Order Status in the builder, save, and ensure the
          store uses a supported ecommerce type.
        </p>
        <div className="flex justify-end">
          <Button type="button" variant="secondary" onClick={closeOrderStatus}>
            Close
          </Button>
        </div>
      </div>,
    )
  }

  return shell(
          <form
            className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-3"
            onSubmit={submit}
          >
            <label className="text-sm">
              <span className="mb-1.5 block font-medium text-[var(--color-surface-foreground)]">
                {orderLabel}
              </span>
              <input
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-surface-foreground)] outline-none"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                autoComplete="off"
              />
            </label>

            {allowed.length > 1 ? (
              <label className="text-sm">
                <span className="mb-1.5 block font-medium text-[var(--color-surface-foreground)]">
                  {t('orderStatus.verifyWith', lang)}
                </span>
                <select
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                  value={verificationType}
                  onChange={(e) => setVerificationType(e.target.value)}
                >
                  {allowed.map((k) => (
                    <option key={k} value={k}>
                      {verificationLabel(k, lang)}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            <label className="text-sm">
              <span className="mb-1.5 block font-medium text-[var(--color-surface-foreground)]">
                {verificationLabel(verificationType, lang)}
              </span>
              <input
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-surface-foreground)] outline-none"
                value={verificationValue}
                onChange={(e) => setVerificationValue(e.target.value)}
                type={verificationType === 'email' ? 'email' : 'text'}
                autoComplete="off"
              />
            </label>

            <div className="mt-1 flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={closeOrderStatus}>
                {t('form.cancel', lang)}
              </Button>
              <Button type="submit" disabled={!canSubmit || submitting}>
                {submitting ? t('orderStatus.searching', lang) : submitLabel}
              </Button>
            </div>
          </form>,
  )
}
